/**
 * Custom hook for polling agent job status
 * Automatically polls job status until completion or failure
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';

export interface AgentJobProgress {
  percentage: number;
  currentOperation?: string;
  thinking?: string;
  stepsCompleted?: number;
  stepsTotal?: number;
}

export interface AgentJobStatus {
  id: string;
  conversationId: string;
  agentName: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  progress: AgentJobProgress;
  result?: Record<string, any>;
  error?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  attempts: number;
  metadata?: Record<string, any>;
}

interface UseAgentJobOptions {
  /** Polling interval in milliseconds (default: 1000) */
  pollInterval?: number;
  /** Enable automatic polling (default: true) */
  enabled?: boolean;
  /** Callback when job completes successfully */
  onComplete?: (result: Record<string, any>) => void;
  /** Callback when job fails */
  onError?: (error: string) => void;
}

interface UseAgentJobReturn {
  /** Current job status data */
  job: AgentJobStatus | null;
  /** Loading state (true during initial fetch) */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether the job is currently being polled */
  isPolling: boolean;
  /** Manually refetch job status */
  refetch: () => Promise<void>;
  /** Cancel polling */
  stopPolling: () => void;
  /** Resume polling */
  startPolling: () => void;
}

/**
 * Hook to poll agent job status
 * @param jobId - The agent job ID to poll
 * @param options - Polling options
 */
export const useAgentJob = (
  jobId: string | null | undefined,
  options: UseAgentJobOptions = {}
): UseAgentJobReturn => {
  const {
    pollInterval = 1000,
    enabled = true,
    onComplete,
    onError,
  } = options;

  const [job, setJob] = useState<AgentJobStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasCompletedRef = useRef<boolean>(false);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Keep callback refs up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  /**
   * Fetch job status from API
   */
  const fetchJobStatus = useCallback(async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get(`/agent-jobs/${jobId}`);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Unknown error');
      }

      setJob(data.job);
      setError(null);
      setLoading(false);

      // Check job age - mark as failed if too old and still active
      const MAX_JOB_AGE = 30 * 60 * 1000; // 30 minutes
      const jobAge = Date.now() - new Date(data.job.createdAt).getTime();

      if (data.job.status === 'active' && jobAge > MAX_JOB_AGE) {
        // Job is stale - treat as failed client-side
        const stalledJob = {
          ...data.job,
          status: 'failed' as const,
          error: 'Job timed out or server restarted',
        };
        setJob(stalledJob);

        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
        }

        // Notify via error callback only once
        if (!hasCompletedRef.current && onErrorRef.current) {
          hasCompletedRef.current = true;
          onErrorRef.current('Job timed out or server restarted');
        }

        return; // Stop processing
      }

      // Check if job is terminal
      if (data.job.status === 'completed' || data.job.status === 'failed') {
        // Stop polling on terminal state
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
        }

        // Call callbacks only once
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;

          if (data.job.status === 'completed' && onCompleteRef.current) {
            onCompleteRef.current(data.job.result || {});
          } else if (data.job.status === 'failed' && onErrorRef.current) {
            onErrorRef.current(data.job.error || 'Job failed');
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch job status';
      setError(errorMessage);
      setLoading(false);

      // Stop polling on error
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setIsPolling(false);
      }
    }
  }, [jobId]);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    if (!jobId || !enabled) return;

    // Don't start polling if job is already terminal
    if (job && (job.status === 'completed' || job.status === 'failed')) {
      return;
    }

    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Start new polling interval
    setIsPolling(true);
    pollIntervalRef.current = setInterval(fetchJobStatus, pollInterval);
  }, [jobId, enabled, job, fetchJobStatus, pollInterval]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setIsPolling(false);
    }
  }, []);

  /**
   * Manually refetch job status
   */
  const refetch = useCallback(async () => {
    setLoading(true);
    await fetchJobStatus();
  }, [fetchJobStatus]);

  // Initial fetch and start polling
  useEffect(() => {
    if (!jobId || !enabled) {
      setLoading(false);
      return;
    }

    // Reset state for new job
    hasCompletedRef.current = false;
    setError(null);
    setLoading(true);

    // Fetch immediately
    fetchJobStatus();

    // Start polling
    startPolling();

    // Cleanup
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobId, enabled, fetchJobStatus, startPolling]);

  return {
    job,
    loading,
    error,
    isPolling,
    refetch,
    stopPolling,
    startPolling,
  };
};
